import { push } from "react-router-redux";
import { backToFront, frontToBackRelationship } from "./dataConversion";
import { fieldErrorsBackToFront } from "./../misc/dataConversion";
import AcctsRepository from "./AcctsRepository";
import logoutCheck from "assets/scripts/utils/logoutCheck";
import mapBackendErrors from "assets/scripts/utils/mapBackendErrors";
import { backToUrl } from "../misc/miscActions";
import MiscRepository from "assets/scripts/domain/misc/MiscRepository";
import { recordEvent } from "api/session";
import { groupAccountsAll } from "assets/scripts/components/containers/secure/Dashboard/groupAccounts";
import isSettlementEligible from "assets/scripts/domain/accts/isSettlementEligible";
/* --- Action names --- */
export const ACCTS_ACTIONS = {
    ACCTS_LOAD_FAILED: "accts-load-failed",
    ACCTS_LOAD_STARTED: "accts-load-started",
    ACCTS_LOAD_SUCCEEDED_FALSE: "accts-load-succeeded-false",
    ACCTS_LOAD_SUCCEEDED_TRUE: "accts-load-succeeded-true",
    SET_ACCOUNT_TO_EDIT: "set-account-to-edit",
    UPDATE_RELATIONSHIP_FAILED: "update-relationship-failed",
    UPDATE_RELATIONSHIP_STARTED: "update-relationship-started",
    UPDATE_RELATIONSHIP_SUCCEEDED_FALSE: "update-relationship-succeeded-false",
    UPDATE_RELATIONSHIP_SUCCEEDED_TRUE: "update-relationship-succeeded-true",
    CREATE_CALLBACK_STARTED: "save-callback-started",
    CREATE_CALLBACK_SUCCEEDED: "save-callback-succeeded",
    CREATE_CALLBACK_FAILED: "save-callback-failed",
    LOAD_RESTRICTIONS_STARTED: "load-restrictions-started",
    LOAD_RESTRICTIONS_SUCCEEDED: "load-restrictions-succeeded",
    LOAD_RESTRICTIONS_FAILED: "load-restrictions-failed",
    LOAD_EMAILOPTOUTS_STARTED: "load-emailoptouts-started",
    LOAD_EMAILOPTOUTS_SUCCEEDED: "load-emailoptouts-succeeded",
    LOAD_EMAILOPTOUTS_FAILED: "load-emailoptouts-failed",
    ADD_PROBATE_CASE_STARTED: "add-probate-case-started",
    ADD_PROBATE_CASE_SUCCEEDED: "add-probate-case-succeeded",
    ADD_PROBATE_CASE_FAILED: "add-probate-case-failed",
    CANCEL_CALLBACK_STARTED: "cancel-callback-started",
    CANCEL_CALLBACK_SUCCEEDED: "cancel-callback-succeeded",
    CANCEL_CALLBACK_FAILED: "cancel-callback-failed",
    SETTLEMENT_OFFER_UPDATE: "settlement-offer-updatedd",
    EMAIL_OPT_OUT_STARTED: "email-opt-out-started",
    EMAIL_OPT_OUT_FAILED: "email-opt-out-failed",
    EMAIL_OPT_OUT_SUCCEEDED_FALSE: "email-opt-out-succeeded-false",
    EMAIL_OPT_OUT_SUCCEEDED_TRUE: "email-opt-out-succeeded-true",
};
/* --- For use on the Dashboard Page --- */
/**
 * Dispatches various actions while loading current user's associated account data
 *
 * @method loadAccts
 * @returns {Function} Redux thunk
 */
export const loadAccts = () => async (dispatch) => {
    dispatch({ type: ACCTS_ACTIONS.ACCTS_LOAD_STARTED });
    let response;
    try {
        response = await AcctsRepository.getAll();
    }
    catch (error) {
        dispatch({ type: ACCTS_ACTIONS.ACCTS_LOAD_FAILED, payload: error });
        logoutCheck(error, dispatch);
        return;
    }
    if (!response.success) {
        const preparedErrors = fieldErrorsBackToFront(response.errors);
        dispatch({ type: ACCTS_ACTIONS.ACCTS_LOAD_SUCCEEDED_FALSE, payload: preparedErrors });
    }
    else {
        const preparedData = backToFront(response.data) || [];
        dispatch({
            type: ACCTS_ACTIONS.ACCTS_LOAD_SUCCEEDED_TRUE,
            payload: {
                accts: preparedData,
                autoAddedPayable: response.data.autoAddedPayable
            }
        });
        const grouped = groupAccountsAll(preparedData, "en");
        const settlementEligible = grouped.filter(it => isSettlementEligible(it));
        if (!!settlementEligible.length) {
            recordEvent({
                action: "ClientEvent",
                data: JSON.stringify({
                    settlementAvailable: settlementEligible.map(it => it.primary)
                })
            });
        }
    }
};
/**
 * Saves the id of the account's relationship to be changed
 *
 * @method setAccountToEdit
 * @param {Number} accountId Id of the account to edit
 * @returns {Function} Redux thunk
 */
export const setAccountToEdit = (accountId) => async (dispatch) => {
    dispatch({
        type: ACCTS_ACTIONS.SET_ACCOUNT_TO_EDIT,
        payload: accountId
    });
};
/**
 * Dispatches a series of actions to update the relationship of the account
 *
 * @method updateRelationship
 * @param {Number} accountId Id of the account to change the relationship of
 * @param {String} relationship Relationship to change to
 * @returns {Function} Redux thunk
 */
export const updateRelationship = (accountId, relationship) => async (dispatch) => {
    dispatch({ type: ACCTS_ACTIONS.UPDATE_RELATIONSHIP_STARTED });
    const preparedRelationshipData = frontToBackRelationship(accountId, relationship);
    let response;
    try {
        response = await AcctsRepository.updateRelationship(preparedRelationshipData);
    }
    catch (error) {
        dispatch({ type: ACCTS_ACTIONS.UPDATE_RELATIONSHIP_FAILED, payload: error });
        logoutCheck(error, dispatch);
        return;
    }
    if (!response.success) {
        const preparedErrors = fieldErrorsBackToFront(response.errors);
        dispatch({
            type: ACCTS_ACTIONS.UPDATE_RELATIONSHIP_SUCCEEDED_FALSE,
            payload: preparedErrors
        });
    }
    else {
        const preparedData = backToFront(response.data) || [];
        dispatch({
            type: ACCTS_ACTIONS.UPDATE_RELATIONSHIP_SUCCEEDED_TRUE,
            payload: {
                accts: preparedData,
                autoAddedPayable: response.data.autoAddedPayable
            }
        });
    }
};
export const createCallback = (callback) => async (dispatch) => {
    dispatch({ type: ACCTS_ACTIONS.CREATE_CALLBACK_STARTED });
    let response;
    try {
        response = await AcctsRepository.createCallback(callback);
    }
    catch (error) {
        dispatch({
            type: ACCTS_ACTIONS.CREATE_CALLBACK_FAILED,
            payload: error
        });
        logoutCheck(error, dispatch);
        return;
    }
    dispatch({
        type: ACCTS_ACTIONS.CREATE_CALLBACK_SUCCEEDED,
        payload: response
    });
    if (response.success && response.data.type === "Success") {
        dispatch(push(`/secured/account/${callback.payableId}`));
    }
};
export const loadRestrictions = () => async (dispatch) => {
    dispatch({ type: ACCTS_ACTIONS.LOAD_RESTRICTIONS_STARTED });
    let response;
    try {
        response = await AcctsRepository.loadCallbackRestrictions();
        if (!response.success) {
            throw new Error("Unexpected result");
        }
    }
    catch (error) {
        dispatch({
            type: ACCTS_ACTIONS.LOAD_RESTRICTIONS_FAILED,
            payload: error
        });
        logoutCheck(error, dispatch);
        return;
    }
    dispatch({
        type: ACCTS_ACTIONS.LOAD_RESTRICTIONS_SUCCEEDED,
        payload: response.data
    });
};
export const backToAcct = (id) => {
    return backToUrl(`/secured/account/${id}`);
};
export const addProbateCase = (payableId, probateCase) => async (dispatch) => {
    dispatch({ type: ACCTS_ACTIONS.ADD_PROBATE_CASE_STARTED });
    let response;
    try {
        response = await AcctsRepository.addProbateCase(payableId, probateCase);
    }
    catch (error) {
        dispatch({
            type: ACCTS_ACTIONS.ADD_PROBATE_CASE_FAILED,
            payload: error
        });
        logoutCheck(error, dispatch);
        return;
    }
    dispatch({
        type: ACCTS_ACTIONS.ADD_PROBATE_CASE_SUCCEEDED,
        payload: response.success
            ? {
                success: true,
                data: backToFront({ accounts: response.data })
            }
            : {
                success: false,
                errors: mapBackendErrors(fieldErrorsBackToFront(response.errors))
            }
    });
    if (response.success) {
        dispatch(push(`/secured/account/${payableId}`));
        MiscRepository.trackEvent({
            event: "CaseDataComplete",
            payableId: payableId.toString()
        });
    }
};
export const cancelCallback = ({ id, payableId }) => async (dispatch, getState) => {
    dispatch({ type: ACCTS_ACTIONS.CANCEL_CALLBACK_STARTED });
    let response;
    try {
        response = await AcctsRepository.cancelCallback(id);
        if (!response.success) {
            throw new Error("Unexpected response");
        }
    }
    catch (error) {
        dispatch({
            type: ACCTS_ACTIONS.CANCEL_CALLBACK_FAILED,
            payload: error
        });
        logoutCheck(error, dispatch);
        return;
    }
    const cancelled = response.data;
    dispatch({
        type: ACCTS_ACTIONS.CANCEL_CALLBACK_SUCCEEDED,
        payload: {
            id,
            cancelled
        }
    });
    if (cancelled) {
        const url = `/secured/account/${payableId}`;
        if (getState().routing.locationBeforeTransitions.pathname.toLowerCase() !== url) {
            dispatch(push(url));
        }
    }
};
export const settlementInfoUpdate = (payableId, info) => ({
    type: ACCTS_ACTIONS.SETTLEMENT_OFFER_UPDATE,
    payload: {
        payableId,
        info
    }
});
/**
 * Dispatches series of actions while sending public optOutEmail info to API
 *
 * @method optOutEmailPublic
 * @param {string} token Opt Out Email token
 * @param {boolean} optOut Opt Out Email value
 * @returns {Function} Redux thunk
 */
export const optOutEmailPublic = (token) => async (dispatch) => {
    dispatch({
        type: ACCTS_ACTIONS.EMAIL_OPT_OUT_STARTED,
        payload: {
            token
        }
    });
    let response;
    try {
        response = await AcctsRepository.emailOptOutPublic(token);
    }
    catch (error) {
        return dispatch({ type: ACCTS_ACTIONS.EMAIL_OPT_OUT_FAILED, payload: error });
    }
    if (!response.success) {
        const preparedErrors = fieldErrorsBackToFront(response.errors);
        dispatch({ type: ACCTS_ACTIONS.EMAIL_OPT_OUT_SUCCEEDED_FALSE, payload: preparedErrors });
    }
    else {
        dispatch({
            type: ACCTS_ACTIONS.EMAIL_OPT_OUT_SUCCEEDED_TRUE,
            payload: {
                refNums: null
            }
        });
        dispatch(push("/public/thankYouEmail"));
    }
};
/**
 * Dispatches series of actions while sending public optOutEmail info to API
 *
 * @method optOutEmailPublic
 * @param {number[]} refNums Opted out reference numbers
 * @returns {Function} Redux thunk
 */
export const optOutEmailSecure = (refNums) => async (dispatch) => {
    dispatch({
        type: ACCTS_ACTIONS.EMAIL_OPT_OUT_STARTED,
        payload: {
            refNums
        }
    });
    let response;
    try {
        response = await AcctsRepository.emailOptOutSecure(refNums);
    }
    catch (error) {
        return dispatch({ type: ACCTS_ACTIONS.EMAIL_OPT_OUT_FAILED, payload: error });
    }
    if (!response.success) {
        const preparedErrors = fieldErrorsBackToFront(response.errors);
        dispatch({ type: ACCTS_ACTIONS.EMAIL_OPT_OUT_SUCCEEDED_FALSE, payload: preparedErrors });
    }
    else {
        dispatch({
            type: ACCTS_ACTIONS.EMAIL_OPT_OUT_SUCCEEDED_TRUE,
            payload: {
                refNums
            }
        });
        dispatch(push("/secured/thankYouEmail"));
    }
};
export const loadEmailOptOuts = () => async (dispatch) => {
    dispatch({ type: ACCTS_ACTIONS.LOAD_EMAILOPTOUTS_STARTED });
    let response;
    try {
        response = await AcctsRepository.loadOptedOutEmailRefNums();
        if (!response.success) {
            throw new Error("Unexpected result");
        }
    }
    catch (error) {
        dispatch({
            type: ACCTS_ACTIONS.LOAD_EMAILOPTOUTS_FAILED,
            payload: error
        });
        logoutCheck(error, dispatch);
        return;
    }
    dispatch({
        type: ACCTS_ACTIONS.LOAD_EMAILOPTOUTS_SUCCEEDED,
        payload: response.data
    });
    return response.data;
};



// WEBPACK FOOTER //
// ./src/assets/scripts/domain/accts/acctsActions.ts